import React, { Fragment } from "react";
import { Field, FieldProps, Form, FormikProps } from "formik";
import { WithTranslation, withTranslation } from "react-i18next";
import classnames from "classnames";

import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import IconClose from "@material-ui/icons/Close";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { Category } from "../../../../models/prize/category/Category";
import { EditCategoryFormValues as FormValues } from "./Edit";

const FormComponent = ({
  formikProps,
  formikProps: { errors, touched, isSubmitting, setFieldValue },
  category,
  close,
  t,
  translationPath,
  supplierCategories
}: {
  formikProps: FormikProps<FormValues>;
  category: Category;
  close: (event: any) => void;
  translationPath: string;
  supplierCategories: any[];
} & WithTranslation) => (
  <Form className="d-flex flex-column h-100">
    <div className="d-flex flex-column h-100">
      <div
        className="d-flex py-2 px-3 justify-content-between align-items-center"
        style={{ borderBottom: "1px solid #dadce0" }}
      >
        <IconButton onClick={close}>
          <IconClose fontSize="small" />
        </IconButton>
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          size="small"
          className="p-2"
          disabled={!formikProps.isValid || isSubmitting}
        >
          {t(`${translationPath}.saveButton`)}
        </Button>
      </div>
      <div
        className="flex-grow-1 py-4 px-5 overflow-auto"
        style={{ backgroundColor: "rgb(241, 243, 244)" }}
      >
        <Fragment>
          <section className="box rounded p-3 mb-2">
            <p className="box__description mb-1">
              {t(`${translationPath}.fields.name.label`)}
            </p>
            <section className="d-flex flex-column">
              <Field
                name="name"
                render={({ field }: FieldProps<FormValues>) => (
                  <TextField
                    {...field}
                    inputProps={{
                      className: "px-2"
                    }}
                    className={classnames("bg-white m-0", {
                      "is-valid": !errors.name && touched.name,
                      "is-invalid": errors.name && touched.name
                    })}
                    tabIndex={1}
                  />
                )}
              />
              {errors.name && touched.name && (
                <div className="d-flex invalid-feedback">
                  {t(`${translationPath}.fields.name.error`)}
                </div>
              )}
            </section>
          </section>

          {!!category.parent && (
            <section className="box rounded p-3">
              <p className="box__description mb-1">
                {t(`${translationPath}.fields.supplierCategories.label`)}{" "}
                <small>
                  (
                  {t(
                    `${translationPath}.fields.supplierCategories.description`
                  )}
                  )
                </small>
              </p>
              <section className="d-flex flex-column">
                <Field
                  name="supplierCategories"
                  render={({ field }: FieldProps<FormValues>) => {
                    const idToNameMap = new Map<string, string>();
                    const nameToIdMap = new Map<string, string>();
                    supplierCategories.forEach(c => {
                      const id = `${c.supplierName}:${c.categoryId}`;
                      const name = c.name.pl;
                      idToNameMap.set(id, name);
                      nameToIdMap.set(name, id);
                    });

                    return (
                      <Autocomplete
                        multiple
                        freeSolo
                        options={supplierCategories.map(c => c.name.pl)}
                        value={
                          field.value
                            ? field.value
                                .split(",")
                                .map((s: string) => s.trim())
                                .filter((s: string) => s)
                                .map((id: string) => idToNameMap.get(id) || id)
                            : []
                        }
                        onChange={(_e, newValue) => {
                          const ids = newValue
                            .map(name => nameToIdMap.get(name) || name)
                            .join(", ");
                          setFieldValue("supplierCategories", ids);
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            multiline
                            inputProps={{
                              ...params.inputProps,
                              className: "px-2"
                            }}
                            className="bg-white m-0"
                            tabIndex={2}
                          />
                        )}
                      />
                    );
                  }}
                />
              </section>
            </section>
          )}
        </Fragment>
      </div>
    </div>
  </Form>
);

export default withTranslation()(FormComponent);
